import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from "react"

/**
 * How AppShell `main` should scroll/pad. Set by layout shells (SidePeekLayout,
 * DenseListPage, MasterDetail flush) — not by URL lists in AppShell.
 *
 * - default: shared-toolbar padded / ordinary main
 * - scrollport: dual-axis overflow on main (@container for sticky table chrome)
 * - flush: p-0 + overflow-hidden (master-detail / nested inner scroll)
 */
export type AppMainMode = "default" | "scrollport" | "flush"

type Entry = { id: number; mode: Exclude<AppMainMode, "default"> }

type AppMainModeApi = {
  register: (id: number, mode: Exclude<AppMainMode, "default">) => void
  unregister: (id: number) => void
}

const AppMainModeStateContext = createContext<AppMainMode>("default")
const AppMainModeApiContext = createContext<AppMainModeApi | null>(null)

let nextMainModeId = 1

function resolveMode(entries: Entry[]): AppMainMode {
  if (entries.some((e) => e.mode === "scrollport")) return "scrollport"
  if (entries.some((e) => e.mode === "flush")) return "flush"
  return "default"
}

export function AppMainModeProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<Entry[]>([])

  const register = useCallback((id: number, mode: Exclude<AppMainMode, "default">) => {
    setEntries((prev) => {
      const without = prev.filter((e) => e.id !== id)
      return [...without, { id, mode }]
    })
  }, [])

  const unregister = useCallback((id: number) => {
    setEntries((prev) => prev.filter((e) => e.id !== id))
  }, [])

  const api = useMemo(() => ({ register, unregister }), [register, unregister])
  const mode = resolveMode(entries)

  return (
    <AppMainModeApiContext.Provider value={api}>
      <AppMainModeStateContext.Provider value={mode}>{children}</AppMainModeStateContext.Provider>
    </AppMainModeApiContext.Provider>
  )
}

/** Register a main scroll/pad mode for the lifetime of the calling layout. */
export function useAppMainMode(mode: Exclude<AppMainMode, "default">) {
  const api = useContext(AppMainModeApiContext)

  useLayoutEffect(() => {
    if (!api) return
    const id = nextMainModeId++
    api.register(id, mode)
    return () => api.unregister(id)
  }, [api, mode])
}

export function useAppMainModeState(): AppMainMode {
  return useContext(AppMainModeStateContext)
}
